import React, { useState, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import Grid from '@mui/joy/Grid';
import Switch from '@mui/joy/Switch';
import Tooltip from '@mui/joy/Tooltip';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Snackbar from '@mui/joy/Snackbar';
import Alert from '@mui/joy/Alert';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { getSettings, setSettings, clearSettingsCache } from '../../utils/settings';

export default function AutomationSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [settings, setSettingsState] = useState({
    seedsPerPack: 10,
    enableAutoProcessing: false,
    autoProcessingInterval: 60, // in minutes
    notifyOnLowStock: true,
    materialsMappings: {
      seedPack: {
        label: 'Seed Pack Label',
        bag: 'Seed Pack Bag',
        jar: 'Makeup Jar',
        pad: 'Diffuser Pad'
      },
      clone: {
        shipper: 'Clone Shipper',
        box: 'USPS Priority Box'
      },
      shipping: {
        domestic: {
          shipper: 'Padded Shipper',
          label: 'Postage Label'
        },
        international: {
          shipper: 'International Shipper',
          label: 'International Postage Label'
        }
      }
    }
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const showSnackbar = (msg, severity = 'success') => {
    setSnackbar({
      open: true,
      message: msg,
      severity
    });
  };
  
  // Load settings on component mount
  useEffect(() => {
    async function loadSettings() {
      try {
        clearSettingsCache(); // Ensure we get fresh data
        const data = await getSettings();
        
        if (data.automation) {
          const newSettings = {
            ...settings,
            ...data.automation
          };
          setSettingsState(newSettings);
          setOriginalSettings(JSON.parse(JSON.stringify(newSettings))); // Deep copy
        } else {
          setOriginalSettings(JSON.parse(JSON.stringify(settings))); // Deep copy of defaults
        }
        
      } catch (error) {
        console.error('Error loading automation settings:', error);
        showSnackbar('Failed to load settings', 'danger');
        setOriginalSettings(JSON.parse(JSON.stringify(settings))); // Deep copy of defaults
      } finally {
        setLoading(false);
      }
    }
    
    loadSettings();
  }, []);
  
  // Save settings
  const handleSave = async () => {
    try {
      setLoading(true);
      const allSettings = await getSettings();
      
      // Update only the automation section
      await setSettings({
        ...allSettings,
        automation: settings
      });
      
      showSnackbar('Settings saved successfully');
    } catch (error) {
      console.error('Error saving automation settings:', error);
      showSnackbar('Failed to save settings', 'danger');
    } finally {
      setLoading(false);
    }
  };
  
  // Clear changes - reset to original loaded settings
  const handleClearChanges = () => {
    if (originalSettings) {
      setSettingsState(JSON.parse(JSON.stringify(originalSettings))); // Deep copy
      showSnackbar('Changes cleared successfully', 'warning');
    }
  };

  // Reset to defaults
  const handleReset = () => {
    setSettingsState({
      seedsPerPack: 10,
      enableAutoProcessing: false,
      autoProcessingInterval: 60,
      notifyOnLowStock: true,
      materialsMappings: {
        seedPack: {
          label: 'Seed Pack Label',
          bag: 'Seed Pack Bag',
          jar: 'Makeup Jar',
          pad: 'Diffuser Pad'
        },
        clone: {
          shipper: 'Clone Shipper',
          box: 'USPS Priority Box'
        },
        shipping: {
          domestic: {
            shipper: 'Padded Shipper',
            label: 'Postage Label'
          },
          international: {
            shipper: 'International Shipper',
            label: 'International Postage Label'
          }
        }
      }
    });
  };
  
  // Handle field changes
  const handleChange = (field, value) => {
    setSettingsState(prev => {
      // Handle nested paths like materialsMappings.seedPack.label
      if (field.includes('.')) {
        const parts = field.split('.');
        const newSettings = { ...prev };
        let current = newSettings;
        
        // Navigate to the right nesting level
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]];
        }
        
        // Set the value
        current[parts[parts.length - 1]] = value;
        return newSettings;
      }
      
      // Simple field
      return { ...prev, [field]: value };
    });
  };
  
  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography level="h3" sx={{ color: 'white', mb: 1 }}>
          Order Automation
        </Typography>
        <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
          Automate order processing and inventory management
        </Typography>
      </Box>

      <Sheet 
        variant="soft"
      sx={{ 
        p: { xs: 2, md: 3 }, 
          borderRadius: 'lg', 
        bgcolor: '#111',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        mb: 4
      }}
    >
      <Box sx={{ mb: 3 }}>
          <Typography level="h4" sx={{ fontWeight: 'bold', color: 'white', mb: { xs: 2, md: 0 }, textAlign: { xs: 'center', md: 'left' } }}>
          Inventory Calculation Settings
        </Typography>
        
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: { xs: 2, md: 0 }, alignItems: { xs: 'center', md: 'flex-end' }, justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <Button
            variant="outlined"
            onClick={handleClearChanges}
            disabled={!originalSettings || JSON.stringify(settings) === JSON.stringify(originalSettings)}
            sx={{
              borderColor: 'rgba(255, 152, 0, 0.5)',
              color: '#ff9800',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                borderColor: '#ff9800',
                bgcolor: 'rgba(255, 152, 0, 0.1)'
              }
            }}
          >
            Clear Changes
          </Button>

          <Button
            variant="outlined"
              startDecorator={<RestartAltIcon />}
            onClick={handleReset}
            sx={{
                borderColor: 'rgba(255, 152, 0, 0.5)',
              color: '#ff9800',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                borderColor: '#ff9800',
                  bgcolor: 'rgba(255, 152, 0, 0.1)'
              }
            }}
          >
            Reset to Defaults
          </Button>
          
          <Button
              variant="solid"
              startDecorator={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            sx={{
              bgcolor: '#4caf50',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                bgcolor: '#388e3c'
              }
            }}
          >
            Save Settings
          </Button>
          </Stack>
      </Box>
      
        <Divider sx={{ mb: 3 }} />
      
      {/* General Automation Settings */}
      <Box sx={{ mb: 4 }}>
          <Typography level="title-lg" sx={{ fontWeight: 'bold', color: 'white', mb: 2 }}>
          General Settings
        </Typography>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Switch
                  checked={settings.enableAutoProcessing}
                  onChange={(e) => handleChange('enableAutoProcessing', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-track': {
                      backgroundColor: settings.enableAutoProcessing ? '#4caf50' : '#666666',
                      opacity: 1
                    },
                    '& .MuiSwitch-thumb': {
                      backgroundColor: '#ffffff'
                    }
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ color: 'white' }}>Enable Automatic Order Data Import</Typography>
                  <Tooltip title="When enabled, the system will automatically fetch order data and update inventory quantities based on your settings">
                    <IconButton size="sm" sx={{ ml: 1, color: 'text.secondary' }}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
          </Grid>
          
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel sx={{ color: 'white' }}>Seeds Per Pack</FormLabel>
                <Input
              type="number"
              value={settings.seedsPerPack}
              onChange={(e) => handleChange('seedsPerPack', parseInt(e.target.value) || 10)}
                  slotProps={{
                    input: { min: 1 }
                  }}
              sx={{
                    borderRadius: 'md',
                    '& .MuiInput-root': {
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                      '&.Mui-focused': {
                    borderColor: '#2196f3',
                  },
                },
              }}
            />
                <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Number of unpacked seeds that go into each seed pack
                </Typography>
              </FormControl>
          </Grid>
          
          {settings.enableAutoProcessing && (
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel sx={{ color: 'white' }}>Data Import Interval (minutes)</FormLabel>
                  <Input
                type="number"
                value={settings.autoProcessingInterval}
                onChange={(e) => handleChange('autoProcessingInterval', parseInt(e.target.value) || 60)}
                    slotProps={{
                      input: { min: 5 }
                    }}
                    sx={{ borderRadius: 'md' }}
                  />
                  <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    How often to check for new order data (in minutes)
                  </Typography>
                </FormControl>
            </Grid>
          )}
          
            <Grid xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Switch
                  checked={settings.notifyOnLowStock}
                  onChange={(e) => handleChange('notifyOnLowStock', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-track': {
                      backgroundColor: settings.notifyOnLowStock ? '#4caf50' : '#666666',
                      opacity: 1
                    },
                    '& .MuiSwitch-thumb': {
                      backgroundColor: '#ffffff'
                    }
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ color: 'white' }}>Notify on Low Stock</Typography>
                  <Tooltip title="Get notifications when processing an order would reduce stock below the reorder threshold">
                    <IconButton size="sm" sx={{ ml: 1, color: 'text.secondary' }}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
          </Grid>
        </Grid>
      </Box>
      
      {/* Material Mappings */}
      <Box sx={{ mb: 3 }}>
          <Typography level="title-lg" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
          Packaging Materials Mapping
        </Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary', mb: 2 }}>
          Configure how order items map to packaging materials in your inventory
        </Typography>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Seed Pack Materials */}
            <Grid xs={12}>
              <Typography level="title-md" sx={{ color: 'white', mb: 2 }}>
              Seed Pack Materials
            </Typography>
            <Grid container spacing={{ xs: 1, md: 2 }}>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>Label Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.seedPack?.label || ''}
                  onChange={(e) => handleChange('materialsMappings.seedPack.label', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>Bag Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.seedPack?.bag || ''}
                  onChange={(e) => handleChange('materialsMappings.seedPack.bag', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>Jar Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.seedPack?.jar || ''}
                  onChange={(e) => handleChange('materialsMappings.seedPack.jar', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>Diffuser Pad Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.seedPack?.pad || ''}
                  onChange={(e) => handleChange('materialsMappings.seedPack.pad', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
            </Grid>
          </Grid>
          
          {/* Clone Materials */}
            <Grid xs={12}>
              <Typography level="title-md" sx={{ color: 'white', mb: 2 }}>
              Clone Materials
            </Typography>
            <Grid container spacing={2}>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>Clone Shipper Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.clone?.shipper || ''}
                  onChange={(e) => handleChange('materialsMappings.clone.shipper', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>USPS Box Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.clone?.box || ''}
                  onChange={(e) => handleChange('materialsMappings.clone.box', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
            </Grid>
          </Grid>
          
          {/* Shipping Materials */}
            <Grid xs={12}>
              <Typography level="title-md" sx={{ color: 'white', mb: 2 }}>
              Shipping Materials
            </Typography>
            <Grid container spacing={2}>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>Domestic Shipper Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.shipping?.domestic?.shipper || ''}
                  onChange={(e) => handleChange('materialsMappings.shipping.domestic.shipper', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>Domestic Label Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.shipping?.domestic?.label || ''}
                  onChange={(e) => handleChange('materialsMappings.shipping.domestic.label', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>International Shipper Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.shipping?.international?.shipper || ''}
                  onChange={(e) => handleChange('materialsMappings.shipping.international.shipper', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <FormControl>
                    <FormLabel sx={{ color: 'white' }}>International Label Name</FormLabel>
                    <Input
                  value={settings.materialsMappings?.shipping?.international?.label || ''}
                  onChange={(e) => handleChange('materialsMappings.shipping.international.label', e.target.value)}
                      sx={{ borderRadius: 'md' }}
                />
                  </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          color={snackbar.severity}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
            color={snackbar.severity}
            variant="solid"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Sheet>
    </Box>
  );
} 